import { Box, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { MouseEvent } from 'react';
import { useShallowSelector } from '../../app/hooks';
import { getCodeNameMapping, selectIsBrandMenu } from '../../selectors/menu';
import { CartItem, CartModifierGroup } from '../../utils/cart';
import Colors from '../../utils/color';
import { DUMMY_MOD_SYMBOL } from '../../utils/constants';
import { getMenuItemPrice } from '../../utils/menu';
import { printCurrency } from '../../utils/money';
import CartItemModGroups from './CartItemModGroups';
import { ParsedModifierGroup } from '../../types/menu';
import { useSelector } from 'react-redux';
import {
  selectDialogHighlightedModifier,
  selectDialogClickedModifier,
  selectDialog,
  selectSelectedItemCartId,
} from '../../selectors/dialog';
import { useAppDispatch } from '../../app/hooks';
import { dialogActions } from '../../reducers/dialogSlice';
import useAutoCombo from '../../hooks/autoCombo/useAutoCombo.hooks';
import {
  MOD_CODES_GROUP_ID,
  MOD_SIZES_GROUP_ID,
} from '../../utils/menuUnification.constants';

interface IChildModGroup {
  modGroup: CartModifierGroup;
  quantity: number;
  cartItem: CartItem;
  isSelected?: boolean;
  selectModGroupToFocus: (e: MouseEvent, modGroup: ParsedModifierGroup) => void;
}

interface IChildModGroupStyling {
  hasChildren: boolean;
  isSelected?: boolean;
}

const useStyles = makeStyles<Theme, IChildModGroupStyling>((theme) => ({
  itemNameClass: {
    wordBreak: 'break-all',
    fontSize: `${theme.spacing(1.5)} !important`,
    // Removed group-level background color - only individual modifiers should be highlighted
  },
  childModGroupClass: {
    fontSize: theme.spacing(1.5),
    paddingBottom: ({ hasChildren }) => (hasChildren ? theme.spacing(0.5) : 0),
  },
  itemPriceClass: {
    fontSize: `${theme.spacing(1.5)} !important`,
    paddingLeft: theme.spacing(2),
  },
}));

export default function ChildModGroup({
  modGroup,
  cartItem,
  quantity,
  isSelected,
  selectModGroupToFocus,
}: IChildModGroup) {
  const dispatch = useAppDispatch();
  const codeNameMapping = useShallowSelector(getCodeNameMapping);
  const highlightedModifier = useSelector(selectDialogHighlightedModifier);
  const clickedModifier = useSelector(selectDialogClickedModifier);
  const { selectedModGroupPath } = useSelector(selectDialog);
  const selectedItemCartId = useSelector(selectSelectedItemCartId);
  const isBrandMenu = useShallowSelector(selectIsBrandMenu);

  const { isUnifiedMenu, isBMAutoComboEnabled } = useAutoCombo();
  const { sortOrder, selectedItems, minimumSelections, name } = modGroup;

  let sortedSelectedItems: CartItem[] = [];
  Object.values(selectedItems || {}).forEach((entry: CartItem) => {
    if (
      isUnifiedMenu &&
      isBMAutoComboEnabled &&
      entry.quantity &&
      entry.quantity > 1
    ) {
      sortedSelectedItems = sortedSelectedItems.concat(
        Array(entry.quantity).fill(entry)
      );
    } else {
      sortedSelectedItems.push(entry);
    }
  });

  if (!isUnifiedMenu) {
    const modSortOrder = sortOrder.reduce(
      (acc: Record<string, number>, sortValue) => {
        const id: number = sortValue.id || 0;
        acc[id.toString()] = sortValue.sortOrder || 0;
        return acc;
      },
      {}
    );
    sortedSelectedItems.sort((a, b) => modSortOrder[a.id] - modSortOrder[b.id]);
  }

  sortedSelectedItems.sort(
    (itemA, itemB) =>
      (itemA.parentModifierGroupIndex || 0) -
      (itemB.parentModifierGroupIndex || 0)
  );

  const hasChildModifierGroups = !!sortedSelectedItems.find(
    (item) => Object.keys(item.childModifierGroups).length
  );

  const { itemNameClass, childModGroupClass, itemPriceClass } = useStyles({
    hasChildren: hasChildModifierGroups,
    isSelected,
  });

  const buildModGroupLabel = (item: CartItem) => {
    let prefix = '',
      showChildModifierGroups = true;
    if (isBrandMenu) {
      const { childModifierGroups } = item;

      // Get all keys from childModifierGroups
      const modifierGroupKeys = Object.keys(childModifierGroups);
      if (modifierGroupKeys.length === 1) {
        const groupName = modifierGroupKeys[0].toLocaleLowerCase();

        if (
          groupName.startsWith(MOD_CODES_GROUP_ID) ||
          groupName.endsWith(MOD_SIZES_GROUP_ID)
        ) {
          const group = childModifierGroups[groupName];
          if (
            group.selectedItems &&
            Object.keys(group.selectedItems).length === 1
          ) {
            // Get the selected item's name
            const firstItemKey = Object.keys(group.selectedItems)[0];
            const selectedItem = group.selectedItems[firstItemKey];

            if (
              selectedItem &&
              selectedItem.childModifierGroups &&
              Object.keys(selectedItem.childModifierGroups).length === 0
            ) {
              prefix = selectedItem.name + ' ';
              showChildModifierGroups = false;
            }
          }
        }
      }
    } else if (
      item.modcode &&
      codeNameMapping[item.modcode]?.code !== DUMMY_MOD_SYMBOL
    ) {
      prefix = codeNameMapping[item.modcode]?.name + ' ';
    }

    return {
      itemLabel: prefix + item.name,
      showChildModifierGroups,
    };
  };

  return (
    <div
      className={`cartitem-child-modgroup ${childModGroupClass}`}
      onClick={(e) => selectModGroupToFocus(e, modGroup)}
    >
      {sortedSelectedItems.length < minimumSelections ? (
        <Typography
          color={Colors.Thunderbird}
          variant="subtitle2"
          children={'! ' + name}
        />
      ) : null}
      {sortedSelectedItems.map((item) => {
        const menuItemPrice = getMenuItemPrice(item, item.modality, quantity);

        // For non-BM items, construct pathToItem if it doesn't exist
        // Check if this is a nested modifier by looking at parentModifierGroupId
        let itemPathToItem = item.pathToItem;
        if (!itemPathToItem) {
          // Non-BM items use simple path format: modGroupId__itemId
          // Cart item scoping is handled by selectedItemCartId check
          itemPathToItem = `${modGroup.id}__${item.id}`;
        }

        // For WASD navigation, also check if the modifier group ID matches directly or if the path ends with the modifier group ID
        // This handles non-BM items where selectedModGroupPath might be just the modGroup.id or a constructed nested path
        const modGroupIdMatches =
          selectedModGroupPath === modGroup.id ||
          (selectedModGroupPath &&
            selectedModGroupPath.endsWith(`__${modGroup.id}`));

        // Check if this item should be highlighted
        const shouldHighlight =
          // Highlight from omnibar search
          (highlightedModifier &&
            itemPathToItem &&
            highlightedModifier === itemPathToItem) ||
          // Highlight from clicking individual modifier - only if it belongs to the currently selected cart item
          (clickedModifier &&
            itemPathToItem &&
            clickedModifier === itemPathToItem &&
            selectedItemCartId === cartItem.cartItemId) ||
          // Highlight from WASD navigation - check if this modifier belongs to the selected modifier group AND the currently selected cart item
          (selectedModGroupPath &&
            selectedItemCartId === cartItem.cartItemId &&
            (itemPathToItem.includes(selectedModGroupPath) ||
              modGroupIdMatches));

        const { itemLabel, showChildModifierGroups } = buildModGroupLabel(item);
        return (
          <div key={`${item.id}-${itemPathToItem}`}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: shouldHighlight ? Colors.alto : '',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                selectModGroupToFocus(e, modGroup);
                // Set the clicked modifier for visual feedback AFTER navigation reset
                dispatch(dialogActions.setClickedModifier(itemPathToItem));
              }}
            >
              <Typography variant="subtitle2" className={itemNameClass}>
                {itemLabel}
              </Typography>
              {menuItemPrice > 0 && (
                <Typography variant="subtitle2" className={itemPriceClass}>
                  {printCurrency(menuItemPrice, 'USD')}
                </Typography>
              )}
            </Box>
            {item && item.childModifierGroups && showChildModifierGroups && (
              <CartItemModGroups
                modGroupCartItem={item}
                cartItem={cartItem}
                quantity={quantity}
                selectable={true}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
